import * as React from "react";
import moment from "moment";
import { CalendarDays as CalendarIcon, ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@components/design-system/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const DATE_DISPLAY_FORMAT = "DD-MM-YYYY";

function formatDateLabel(date: Date) {
  return moment(date).format(DATE_DISPLAY_FORMAT);
}

interface DateRangePickerProps {
  startDate?: Date;
  endDate?: Date;
  onChange?: (dates: [Date | undefined, Date | undefined]) => void;
  placeholder?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

export function DateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholder = "Select Date",
  className,
  minDate,
  maxDate,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [currentView, setCurrentView] = React.useState<"start" | "end">(
    "start"
  );
  const [currentMonth, setCurrentMonth] = React.useState<number>(() =>
    moment().month()
  );
  const [currentYear, setCurrentYear] = React.useState<number>(() =>
    moment().year()
  );
  const [hoverDate, setHoverDate] = React.useState<Date | null>(null);
  const [internalStartDate, setInternalStartDate] = React.useState<
    Date | undefined
  >(startDate);
  const [internalEndDate, setInternalEndDate] = React.useState<
    Date | undefined
  >(endDate);

  React.useEffect(() => {
    setInternalStartDate(startDate);
  }, [startDate]);

  React.useEffect(() => {
    setInternalEndDate(endDate);
  }, [endDate]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const maxSelectableYear = React.useMemo(
    () => (maxDate ? moment(maxDate).year() : moment().year()),
    [maxDate]
  );
  const minSelectableYear = React.useMemo(
    () => (minDate ? moment(minDate).year() : 1975),
    [minDate]
  );
  const years = React.useMemo(
    () =>
      Array.from(
        { length: Math.max(1, maxSelectableYear - minSelectableYear + 1) },
        (_, i) => minSelectableYear + i
      ),
    [minSelectableYear, maxSelectableYear]
  );

  React.useEffect(() => {
    if (currentYear > maxSelectableYear) {
      setCurrentYear(maxSelectableYear);
    } else if (currentYear < minSelectableYear) {
      setCurrentYear(minSelectableYear);
    }
  }, [currentYear, minSelectableYear, maxSelectableYear]);

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentMonth(parseInt(event.target.value));
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentYear(parseInt(event.target.value));
  };

  const handleDayClick = (day: Date) => {
    // Don't allow selection of disabled dates
    if (isDateDisabled(day)) return;

    if (currentView === "start") {
      setInternalStartDate(day);
      setCurrentView("end");
      if (internalEndDate && day > internalEndDate) {
        setInternalEndDate(undefined);
      }
    } else {
      if (internalStartDate && day < internalStartDate) {
        setInternalStartDate(day);
        setInternalEndDate(undefined);
        setCurrentView("end");
      } else {
        setInternalEndDate(day);
        // Call the parent handler with both dates
        // if (onChange) onChange([internalStartDate, day]);
      }
    }
  };

  const handleReset = () => {
    setInternalStartDate(undefined);
    setInternalEndDate(undefined);
    setCurrentView("start");

    // Call the parent handler with both dates as undefined
    if (onChange) onChange([undefined, undefined]);

    setOpen(false);
  };

  const handleDone = () => {
    if (!internalStartDate || !internalEndDate) return;

    if (onChange) onChange([internalStartDate, internalEndDate]);

    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) return;

    if (internalStartDate && !internalEndDate) {
      setCurrentView("end");
      setCurrentMonth(moment(internalStartDate).month());
      setCurrentYear(moment(internalStartDate).year());
      return;
    }

    setCurrentView("start");
    const anchorDate = internalStartDate ?? internalEndDate;
    if (anchorDate) {
      setCurrentMonth(moment(anchorDate).month());
      setCurrentYear(moment(anchorDate).year());
    }
  };

  const getDaysInMonth = (month: number, year: number) => {
    // Get first day of month (0 = Sunday, 1 = Monday, etc)
    let firstDay = moment(new Date(year, month, 1)).startOf("month").day();
    // Adjust for Monday start (convert Sunday from 0 to 6)
    firstDay = firstDay === 0 ? 6 : firstDay - 1;

    const daysInMonth = moment(new Date(year, month, 1)).daysInMonth();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const isDateInRange = (date: Date) => {
    if (!internalStartDate || !internalEndDate) return false;
    return date >= internalStartDate && date <= internalEndDate;
  };

  const isDateSelected = (date: Date) => {
    if (!date) return false;
    return (
      (internalStartDate && moment(date).isSame(internalStartDate, "day")) ||
      (internalEndDate && moment(date).isSame(internalEndDate, "day"))
    );
  };

  const isDateHovered = (date: Date) => {
    if (!date || !hoverDate || !internalStartDate || internalEndDate)
      return false;
    return date > internalStartDate && date <= hoverDate;
  };

  const isDateDisabled = (date: Date) => {
    if (!date) return false;
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const renderTriggerLabel = () => {
    if (internalStartDate && internalEndDate) {
      return (
        <span className="flex min-w-0 flex-1 items-center gap-[8px] leading-[20px]">
          <span className="shrink-0 whitespace-nowrap">
            {formatDateLabel(internalStartDate)}
          </span>
          <span className="shrink-0 text-[#C0C0C0]">-</span>
          <span className="shrink-0 whitespace-nowrap">
            {formatDateLabel(internalEndDate)}
          </span>
        </span>
      );
    }

    if (internalStartDate) {
      return (
        <span className="flex min-w-0 flex-1 items-center gap-[8px] leading-[20px]">
          <span className="shrink-0 whitespace-nowrap text-[#1F1F1F]">
            {formatDateLabel(internalStartDate)}
          </span>
          <span className="shrink-0 text-[#C0C0C0]">-</span>
          <span className="shrink-0 whitespace-nowrap text-[#C0C0C0]">
            Select end date
          </span>
        </span>
      );
    }

    return <span className="text-[#C0C0C0]">{placeholder}</span>;
  };

  const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const daysInMonth = getDaysInMonth(currentMonth, currentYear);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className={cn("relative h-[48px] w-full", className)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex h-full w-full min-w-0 items-center overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white",
              "pl-[24px] pr-[56px] text-left text-[14px] font-medium text-[#1F1F1F]",
              "hover:bg-white focus-visible:outline-none"
            )}
          >
            {renderTriggerLabel()}
          </button>
        </PopoverTrigger>
        <div className="pointer-events-none absolute top-[7px] right-[24px] flex h-[32px] w-[32px] items-center justify-center rounded-full bg-[#f2f2f2] p-[4px]">
          <CalendarIcon className="h-[20px] w-[20px] text-[#1F1F1F]" />
        </div>
      </div>
      <PopoverContent
        align="center"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        sticky="partial"
        className="w-[min(380px,calc(100vw-32px))] p-0 bg-white rounded-lg shadow-md border-[#E5E7EB]"
      >
        <div className="p-4 pb-0">
          <div className="flex gap-2">
            {/* Month selector */}
            <div className="relative min-w-0 flex-1">
              <select
                value={currentMonth}
                onChange={handleMonthChange}
                className="h-[40px] w-full appearance-none rounded-md border-none bg-[#F5FBFF] px-4 pr-8 text-[#1F1F1F] font-bold focus:outline-none"
              >
                {months.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-4 h-4 w-4 pointer-events-none" />
            </div>

            {/* Year selector */}
            <div className="relative min-w-0 flex-1">
              <select
                value={currentYear}
                onChange={handleYearChange}
                className="h-[40px] w-full appearance-none rounded-md border-none bg-[#F5FBFF] px-4 pr-8 text-[#1F1F1F] font-bold focus:outline-none"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-4 h-4 w-4 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="p-3">
          {/* Calendar header - days of week */}
          <div className="grid w-full grid-cols-7">
            {days.map((day) => (
              <div
                key={day}
                className="flex h-[32px] w-full items-center justify-center text-sm font-medium text-[#5C88C5]"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar body - days of month */}
          <div className="grid w-full grid-cols-7 gap-y-1">
            {daysInMonth.map((day, index) => {
              if (!day) {
                return (
                  <div key={`empty-${index}`} className="h-[32px] w-full" />
                );
              }

              const isToday = moment(day).isSame(moment(), "day");
              const isSelected = isDateSelected(day);
              const isInRange = isDateInRange(day) || isDateHovered(day);

              const isDisabled = isDateDisabled(day);
              return (
                <div
                  key={day.toString()}
                  // type="button"
                  onClick={() => !isDisabled && handleDayClick(day)}
                  onMouseEnter={() =>
                    !isDisabled &&
                    currentView === "end" &&
                    internalStartDate &&
                    setHoverDate(day)
                  }
                  onMouseLeave={() => setHoverDate(null)}
                  className={cn(
                    "flex h-[32px] w-full items-center justify-center text-[16px] text-sm font-light transition-colors",
                    isDisabled
                      ? "text-gray-300 cursor-default"
                      : "cursor-pointer",
                    isToday &&
                      !isSelected &&
                      !isDisabled &&
                      "bg-[#f0f0f0] text-[#1F1F1F] font-light",
                    isSelected && "bg-[#5C88C5] text-white font-medium",
                    isInRange &&
                      !isSelected &&
                      !isDisabled &&
                      "bg-[#F5FBFF] text-[#5C88C5]",
                    !isSelected && !isDisabled && "hover:bg-[#f0f0f0]"
                  )}
                >
                  {day.getDate()}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="mb-3 flex items-center justify-center gap-2 px-3">
          <Button
            onClick={handleReset}
            variant="primary-outline"
            className="h-[40px] min-w-0 flex-1 max-w-[150px] text-[#1F1F1F] font-semibold"
            disabled={!internalStartDate && !internalEndDate}
          >
            Reset
          </Button>
          <Button
            onClick={handleDone}
            className="h-[40px] min-w-0 flex-1 max-w-[150px] bg-[#5C88C5] font-semibold text-white"
            disabled={!internalStartDate || !internalEndDate}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
